import { useRef, type FC } from "react"
import { Navigate, useParams } from "react-router"
import styled from "styled-components"

import { parseNumber } from "@forento/shared/utilities/number"

import Button from "~/themes/school/components/Button"
import Certificate from "~/themes/school/components/Certificate"
import Layout from "~/themes/school/components/Layout"
import PageHeader, { PageBreadcrumb } from "~/themes/school/components/PageHeader"
import PartialLoadingPage from "~/themes/school/components/PartialLoadingPage"
import { useTranslation } from "~/translations"
import { downloadElement } from "~/utilities/element"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

const CertificatePage: FC = () => {
	const certificateId = parseNumber(useParams().certificateId)
	const t = useTranslation()

	const downloadRef = useRef<HTMLDivElement>(null)

	const { data: certificate, error } = query.course.certificate.get.useQuery(certificateId ?? -1)

	if (certificate === null) return <Navigate to={routes.certificate.index()} />

	return (
		<Layout>
			<PageHeader>
				<PageBreadcrumb
					title={certificate?.course.title ?? "..."}
					path={[{ title: t("certificate.title"), link: routes.certificate.index() }]}
				/>
				{certificate && (
					<Button
						variant="primary"
						onClick={async () => {
							if (!certificate || !downloadRef.current) return
							await downloadElement(downloadRef.current)
						}}
					>
						{t("general.download")}
					</Button>
				)}
			</PageHeader>
			{error ? (
				<p>{t("failedToLoad")}</p>
			) : !certificate ? (
				<PartialLoadingPage />
			) : (
				<Container ref={downloadRef}>
					<Certificate certificate={certificate} />
				</Container>
			)}
		</Layout>
	)
}

const Container = styled.div`
	max-width: 800px;
	background-color: white;
	box-shadow: 0px 4px 30px 0px rgba(223, 232, 255, 0.25);
	padding: 24px;
`

export default CertificatePage
